import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import {
  usePlanLibraryPlans,
  type PlanLibraryPlan,
} from 'hooks/planLibrary/planLibraryHooks';
import {
  PlanExtrasPreview,
  PlanPickerList,
  planHasContents,
} from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/PlanPicker';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import { useRosterTemplateLibrary } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import {
  useFilledRosterTemplates,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';

import {
  plansForResponsibleParty,
  type CreateIncidentFormValues,
} from './createIncidentModel';
import { useCreateRosterDraft } from './CreateRosterDraftContext';
import { DraftRosterWorkspace } from './DraftRosterWorkspace';

/**
 * HWCG step 2 after Specifics. A plan is required. Selecting one with a filled
 * roster seeds the draft chart below; seats can be edited before create.
 */
export function StepApplyPlan() {
  const { watch, setValue } = useFormContext<CreateIncidentFormValues>();
  const planId = watch('plan_id');
  const companyName = watch('hwcg_specifics.companyName');
  const { data: plans = [], isLoading } = usePlanLibraryPlans();
  const incidentTypes = watch('incident_types');
  const aorIds = watch('areas_of_responsibility');
  const incident = useMemo(
    () => ({ categories: incidentTypes, aorIds }),
    [incidentTypes, aorIds]
  );
  const filledTemplates = useFilledRosterTemplates();
  const library = useRosterTemplateLibrary();
  const catalog = useRosterTemplatesCatalog();
  const { applyFilledRoster } = useCreateRosterDraft();
  const appliedPlanRef = useRef<string | null>(null);

  const visiblePlans = useMemo(
    () => plansForResponsibleParty(plans, companyName),
    [companyName, plans]
  );

  const selected = useMemo(
    () => visiblePlans.find((plan) => plan.id === planId) ?? null,
    [visiblePlans, planId]
  );

  useEffect(() => {
    if (!selected?.filledRosterTemplateId) return;
    if (appliedPlanRef.current === selected.id) return;
    const template = (filledTemplates.data ?? []).find(
      (entry) => entry.id === selected.filledRosterTemplateId
    );
    if (!template) return;
    const nodes = nodesFor(
      template,
      library.data ?? [],
      catalog.data?.templates ?? []
    );
    if (!nodes.length) return;
    applyFilledRosterToDraft({
      template,
      nodes,
      applyFilledRoster,
      setValue,
    });
    appliedPlanRef.current = selected.id;
  }, [
    applyFilledRoster,
    catalog.data?.templates,
    filledTemplates.data,
    library.data,
    selected,
    setValue,
  ]);

  const choose = (plan: PlanLibraryPlan | null) => {
    setValue('plan_id', plan?.id ?? null);
    setValue('plan_name', plan?.name ?? null);
    if (!plan?.filledRosterTemplateId) {
      setValue('hwcg_filled_roster_template_id', null, { shouldDirty: true });
      appliedPlanRef.current = plan?.id ?? null;
    }
  };

  return (
    <div
      className="tw:mx-auto tw:flex tw:h-full tw:w-full tw:max-w-6xl tw:flex-col tw:gap-4 tw:overflow-y-auto tw:py-2"
      data-tour="hwcg-create-apply-plan"
    >
      <div className="tw:shrink-0">
        <h2 className="tw:m-0 tw:text-lg tw:font-semibold">
          Apply Plan <span className="tw:text-destructive">*</span>
        </h2>
        <p className="tw:m-0 tw:mt-1 tw:text-sm tw:text-muted-foreground">
          Choose a plan for this responsible party. Its filled roster loads
          below so you can adjust seats before creating the incident. Tasks and
          ICS forms are created after the workspace exists.
        </p>
      </div>

      {isLoading ? (
        <p className="tw:text-sm tw:text-muted-foreground">Loading plans…</p>
      ) : visiblePlans.length === 0 ? (
        <p className="tw:text-sm tw:text-muted-foreground">
          No plans match this responsible party yet. Tag a plan in Hub → Plan
          Library, or create an untagged plan.
        </p>
      ) : (
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3">
          <PlanPickerList
            className="tw:max-h-48 tw:shrink-0"
            plans={visiblePlans}
            incident={incident}
            selectedId={planId ?? null}
            onSelect={(plan) => choose(plan)}
          />

          {selected ? (
            !planHasContents(selected) && !selected.filledRosterTemplateId ? (
              <p className="tw:shrink-0 tw:text-sm tw:text-muted-foreground">
                This plan has no template tasks, ICS forms, documents, or roster
                templates yet.
              </p>
            ) : (
              <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-2">
                <div className="tw:shrink-0 tw:flex tw:flex-col tw:gap-2">
                  <PlanExtrasPreview plan={selected} />
                  {selected.tasks.length > 0 ? (
                    <ul className="tw:max-h-32 tw:overflow-auto tw:rounded-md tw:border tw:border-border tw:p-2">
                      {selected.tasks.map((task) => (
                        <li
                          key={task.id}
                          className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-1 tw:py-1.5"
                        >
                          <span className="tw:min-w-0 tw:flex-1 tw:text-sm tw:break-words">
                            {task.name}
                          </span>
                          <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
                            {task.assignee_kind === 'position' &&
                            task.assignee_id ? (
                              <Badge variant="outline">{task.assignee_id}</Badge>
                            ) : null}
                            {task.status ? (
                              <Badge variant="outline">{task.status}</Badge>
                            ) : null}
                            <Badge variant="outline">{task.priority}</Badge>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {(selected.forms ?? []).length > 0 ? (
                    <ul className="tw:max-h-28 tw:overflow-auto tw:rounded-md tw:border tw:border-border tw:p-2">
                      {(selected.forms ?? []).map((form) => (
                        <li
                          key={form.id}
                          className="tw:px-1 tw:py-1.5 tw:text-sm"
                        >
                          {form.label}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <div className="tw:min-h-[600px] tw:flex-1 tw:overflow-auto tw:rounded-md tw:border tw:border-border">
                  <DraftRosterWorkspace />
                </div>
              </div>
            )
          ) : (
            <p className="tw:text-sm tw:text-muted-foreground">
              Select a plan to preview its contents and edit the roster.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function applyFilledRosterToDraft({
  template,
  nodes,
  applyFilledRoster,
  setValue,
}: {
  template: FilledRosterTemplate;
  nodes: RosterTemplateNodeDto[];
  applyFilledRoster: ReturnType<
    typeof useCreateRosterDraft
  >['applyFilledRoster'];
  setValue: ReturnType<
    typeof useFormContext<CreateIncidentFormValues>
  >['setValue'];
}) {
  const people = template.assignments.flatMap((row) =>
    row.userGuid
      ? [
          {
            seatName: row.seatName,
            user_guid: row.userGuid,
            name: row.name,
            email_address: row.email,
          },
        ]
      : []
  );
  applyFilledRoster({
    nodes,
    slug: template.sourceSlug || template.sourceTemplateId || template.id,
    name: template.name,
    people,
  });
  setValue(
    'hwcg_roster_externals',
    template.assignments
      .filter((row) => !row.userGuid)
      .map((row) => ({
        seatName: row.seatName,
        name: row.name,
        email: row.email,
        phone: row.phone,
      })),
    { shouldDirty: true }
  );
  setValue('hwcg_filled_roster_template_id', template.id, {
    shouldDirty: true,
  });
}

function nodesFor(
  template: FilledRosterTemplate,
  library: { id: string; nodes: RosterTemplateNodeDto[] }[],
  catalog: { slug: string; nodes?: RosterTemplateNodeDto[] }[]
): RosterTemplateNodeDto[] {
  if (template.sourceTemplateId) {
    return (
      library.find((entry) => entry.id === template.sourceTemplateId)?.nodes ??
      []
    );
  }
  if (template.sourceSlug) {
    return (
      catalog.find((entry) => entry.slug === template.sourceSlug)?.nodes ?? []
    );
  }
  return [];
}
